export const NOTIFY_CODES = [
  { code: 'LM', label: 'LM = LEFT MESSAGE' },
  { code: 'STP', label: 'STP = SPOKE TO PATIENT' },
  { code: 'STO', label: 'STO = SPOKE TO OTHER' },
  { code: 'TXT', label: 'TXT = TEXTED PATIENT' },
  { code: 'EM', label: 'EM = EMAILED' },
];

export const PRODUCTION_OPEN_STATUSES = [
  'FIRST INSPECTION',
  'FIRST INSPECTION NEEDED',
  'FINAL INSPECTION',
  'CUT & EDGE',
  'INSTALL NOSEPADS',
  'REPAIR',
  'ORDER FRAME',
  'TO ORDER',
  'ORDER PART',
  'OTHER / PART',
  'ORDER LENSES',
];

export function formatNotifyStamp(code?: string, date?: string | Date): string {
  const stamp = String(code || '').trim().toUpperCase();
  if (!stamp || !date) {
    return stamp;
  }
  const value = date instanceof Date ? date : new Date(date);
  if (isNaN(value.getTime())) {
    return stamp;
  }
  return `${stamp} - ${value.getMonth() + 1}/${value.getDate()}/${String(
    value.getFullYear()
  ).slice(-2)}`;
}

export function hasOpenProductionTask(order: any): boolean {
  const activity = order?.products?.[0]?.frame?.activity;
  if (!Array.isArray(activity)) {
    return false;
  }
  return activity.some((entry) => {
    if (entry?.completed) {
      return false;
    }
    const status = String(entry?.status || '').trim().toUpperCase();
    return PRODUCTION_OPEN_STATUSES.indexOf(status) !== -1;
  });
}

export function isAwaitingOtherJobs(order: any): boolean {
  if (order?.notification?.code) {
    return false;
  }
  const jobs = order?.linkSummary?.jobs || [];
  const groupIds = (order?.notifyGroupIds || []).map((id) => String(id));
  const preference = String(
    order?.notifyPreference || order?.linkSummary?.notifyPreference || ''
  ).toUpperCase();
  const relevant = groupIds.length
    ? jobs.filter((job) => groupIds.indexOf(String(job.orderId)) !== -1)
    : preference === 'WAIT_ALL'
    ? jobs.filter(
        (job) =>
          String(job.patientId) ===
          String(order?.patient?._id || order?.patient)
      )
    : [];
  if (relevant.length < 2) {
    return (order?.linkSummary?.patientJobCount || 0) > 1 &&
      preference === 'WAIT_ALL' &&
      (order?.linkSummary?.patientReadyCount || 0) <
        (order?.linkSummary?.patientJobCount || 0);
  }
  return relevant.some((job) => !job.ready);
}

export function hasCompletedProductionTask(order: any): boolean {
  const activity = order?.products?.[0]?.frame?.activity;
  if (!Array.isArray(activity)) {
    return false;
  }
  return activity.some((entry) => {
    if (!entry?.completed) {
      return false;
    }
    const status = String(entry?.status || '').trim().toUpperCase();
    return PRODUCTION_OPEN_STATUSES.indexOf(status) !== -1;
  });
}

export function isRecentReadyDate(order: any): boolean {
  const dateReceived = order?.products?.[0]?.frame?.dateReceived;
  if (dateReceived == null || dateReceived === '') {
    return false;
  }
  const raw = String(dateReceived).trim();
  const value = /^\d+$/.test(raw)
    ? Number(raw)
    : new Date(dateReceived).getTime();
  if (isNaN(value)) {
    return false;
  }
  return Date.now() - value <= 120 * 24 * 60 * 60 * 1000;
}

export function isJobReadyCandidate(order: any): boolean {
  const status = String(order?.status || '').trim().toUpperCase();
  const fulfillment = String(order?.fulfillment || 'PICKUP').trim().toUpperCase();
  if (fulfillment === 'SHIP') {
    return false;
  }
  if (status !== 'RECEIVED' && status !== 'TO NOTIFY') {
    return false;
  }
  if (hasOpenProductionTask(order)) {
    return false;
  }
  if (status === 'TO NOTIFY' || order?.notification?.code) {
    return true;
  }
  return hasCompletedProductionTask(order) || isRecentReadyDate(order);
}

export function jobReadyBoardStatus(order: any): string {
  if (isAwaitingOtherJobs(order)) {
    return 'AWAITING OTHER JOBS';
  }
  if (order?.notification?.code) {
    return formatNotifyStamp(order.notification.code, order.notification.date);
  }
  if (order?.redoPriority) {
    return 'RUSH NOTIFY';
  }
  return 'TO NOTIFY';
}

export function visibleJobStatus(order: any): string {
  const stored = String(order?.status || '').trim();
  if (isJobReadyCandidate(order)) {
    return jobReadyBoardStatus(order);
  }
  return stored;
}

export function jobReadySortRank(status: string): number {
  const value = String(status || '').toUpperCase();
  if (value === 'RUSH NOTIFY') {
    return 0;
  }
  if (value === 'TO NOTIFY') {
    return 1;
  }
  if (value === 'AWAITING OTHER JOBS') {
    return 2;
  }
  return 3;
}

export function preferredContact(patient: any): string {
  if (!patient) {
    return '';
  }
  const parts = [];
  if (patient.cell) {
    parts.push(`CELL ${patient.cell}`);
  }
  if (patient.phone) {
    parts.push(`HOME ${patient.phone}`);
  }
  if (patient.email) {
    parts.push(patient.email);
  }
  return parts.join(' · ');
}
